import React, { DataTestAttributes, PropsWithChildren } from 'react';

import { Footer } from '@/components';
import { SignedInNavbar } from '@/components/SignedInNavbar';
import { MenuItemProps } from '@/components/SignedInNavbar/Menu';
import { NavBar, SecondaryNavProps } from '@/components/widgets/o/NavBar/NavBar';
import { useIsConservation } from '@/hooks/useIsConservation';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { useScreenSize } from '@/hooks/useScreenSize';

import { SignedInPageLayoutContentContainer } from './components';

export type SignedInPageProps = {
  items: MenuItemProps[];
  withCustomContentContainer?: boolean;
  navBarProps?: SecondaryNavProps;
  className?: string;
} & DataTestAttributes;

export const SignedInPageLayout: React.FC<PropsWithChildren<SignedInPageProps>> = ({
  children,
  withCustomContentContainer = false,
  navBarProps,
  ...props
}) => {
  const isStaff = useIsStaffUser();

  if (isStaff) {
    return (
      <DefaultLayout withCustomContentContainer={withCustomContentContainer} navBarProps={navBarProps} {...props}>
        {children}
      </DefaultLayout>
    );
  }

  return (
    <PageLayout withCustomContentContainer={withCustomContentContainer} navBarProps={navBarProps} {...props}>
      {children}
    </PageLayout>
  );
};

const PageLayout: React.FC<PropsWithChildren<SignedInPageProps>> = ({
  children,
  withCustomContentContainer = false,
  navBarProps,
  ...props
}) => {
  const isConservation = useIsConservation();

  if (isConservation) {
    return (
      <ConservationLayout withCustomContentContainer={withCustomContentContainer} navBarProps={navBarProps} {...props}>
        {children}
      </ConservationLayout>
    );
  }

  return (
    <DefaultLayout withCustomContentContainer={withCustomContentContainer} navBarProps={navBarProps} {...props}>
      {children}
    </DefaultLayout>
  );
};

const DefaultLayout: React.FC<PropsWithChildren<SignedInPageProps>> = ({
  children,
  withCustomContentContainer,
  navBarProps,
  className,
  'data-testid': dataTestId = 'page-container',
}) => {
  return (
    <div data-testid={dataTestId} className={className}>
      {withCustomContentContainer ? (
        <>
          {navBarProps && <NavBar className='px-10 pt-6' {...navBarProps} />}
          {children}
        </>
      ) : (
        <SignedInPageLayoutContentContainer>
          {navBarProps && <NavBar {...navBarProps} />}
          {children}
        </SignedInPageLayoutContentContainer>
      )}
    </div>
  );
};

const ConservationLayout: React.FC<PropsWithChildren<SignedInPageProps>> = ({
  children,
  withCustomContentContainer = false,
  items,
}) => {
  const isLargeScreen = useScreenSize() === 'large';

  return (
    <div className='flex flex-col h-full'>
      <SignedInNavbar items={items} />
      <div className='w-full flex-1 bg-[#F5F5F3] ' data-testid='page-container'>
        {withCustomContentContainer ? (
          children
        ) : (
          <SignedInPageLayoutContentContainer>{children}</SignedInPageLayoutContentContainer>
        )}
      </div>

      {isLargeScreen && <Footer />}
    </div>
  );
};
